// calculators/true-cost/src/chart.ts
// Hand-rolled SVG balance-over-time area chart.
// No deps. Uses CSS color tokens via currentColor / inline var() references.
// Mobile-friendly via viewBox scaling.

interface MonthPoint {
  month: number;
  payment: number;
  interestPortion: number;
  principalPortion: number;
  remainingBalance: number;
}

const VIEW_W = 600;
const VIEW_H = 280;
const PAD_LEFT = 56;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 36;

const SVG_NS = "http://www.w3.org/2000/svg";

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    node.setAttribute(k, String(v));
  }
  return node;
}

function niceCeil(n: number): number {
  if (n <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(n)));
  const norm = n / mag;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * mag;
}

export function clearChart(): void {
  const svg = document.getElementById("tc-chart") as unknown as SVGSVGElement | null;
  if (!svg) return;
  while (svg.firstChild) svg.removeChild(svg.firstChild);
}

export function renderChart(svg: SVGSVGElement, breakdown: MonthPoint[]): void {
  // Clear
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  svg.setAttribute("viewBox", `0 0 ${VIEW_W} ${VIEW_H}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.setAttribute("role", "img");

  // Prepend a synthetic month=0 point with the starting balance so the area
  // begins at the y-axis. Reconstruct: balance at start of month 1 = balance
  // after month 1 + principalPortion paid in month 1.
  const startBalance = breakdown[0]
    ? breakdown[0].remainingBalance + breakdown[0].principalPortion
    : 0;
  const points: { month: number; remainingBalance: number }[] = [
    { month: 0, remainingBalance: startBalance },
    ...breakdown.map((b) => ({ month: b.month, remainingBalance: b.remainingBalance })),
  ];

  const maxMonth = Math.max(...points.map((p) => p.month), 1);
  const maxBalance = niceCeil(Math.max(...points.map((p) => p.remainingBalance), 1));

  const plotW = VIEW_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VIEW_H - PAD_TOP - PAD_BOTTOM;

  const xFor = (m: number) => PAD_LEFT + (m / maxMonth) * plotW;
  const yFor = (b: number) => PAD_TOP + (1 - b / maxBalance) * plotH;

  // ── Grid lines + Y-axis labels ─────────────────────────────────────────
  const gridGroup = el("g", { "stroke": "#E2E8F0", "stroke-width": 1 });
  const labelGroup = el("g", {
    "font-family": "Inter, system-ui, sans-serif",
    "font-size": 11,
    "fill": "#64748B",
  });

  const Y_TICKS = 4;
  for (let i = 0; i <= Y_TICKS; i++) {
    const v = (maxBalance * i) / Y_TICKS;
    const y = yFor(v);
    const line = el("line", {
      x1: PAD_LEFT,
      x2: VIEW_W - PAD_RIGHT,
      y1: y,
      y2: y,
    });
    gridGroup.appendChild(line);

    const lbl = el("text", {
      x: PAD_LEFT - 8,
      y: y + 4,
      "text-anchor": "end",
    });
    lbl.textContent = `$${Math.round(v).toLocaleString()}`;
    labelGroup.appendChild(lbl);
  }

  // X-axis month labels — show ~5 evenly spaced
  const X_TICKS = Math.min(5, maxMonth);
  for (let i = 0; i <= X_TICKS; i++) {
    const m = Math.round((maxMonth * i) / X_TICKS);
    const x = xFor(m);
    const lbl = el("text", {
      x,
      y: VIEW_H - PAD_BOTTOM + 18,
      "text-anchor": "middle",
    });
    lbl.textContent = `M${m}`;
    labelGroup.appendChild(lbl);
  }

  svg.appendChild(gridGroup);
  svg.appendChild(labelGroup);

  // ── Area path ──────────────────────────────────────────────────────────
  const linePoints = points.map((p) => `${xFor(p.month)},${yFor(p.remainingBalance)}`);
  const linePath = `M ${linePoints.join(" L ")}`;

  // Area: same path, closed down to the baseline
  const baselineY = yFor(0);
  const areaPath =
    linePath +
    ` L ${xFor(maxMonth)},${baselineY}` +
    ` L ${xFor(0)},${baselineY} Z`;

  const area = el("path", {
    d: areaPath,
    fill: "rgba(0, 212, 170, 0.18)",
    stroke: "none",
  });
  svg.appendChild(area);

  const line = el("path", {
    d: linePath,
    fill: "none",
    stroke: "#00876B",
    "stroke-width": 2.5,
    "stroke-linejoin": "round",
    "stroke-linecap": "round",
  });

  // Must append before measuring — getTotalLength() can return 0 on a
  // detached <path> in some browsers, breaking the dash animation.
  svg.appendChild(line);

  const lineNode = line as SVGPathElement;
  const measured =
    typeof lineNode.getTotalLength === "function" ? lineNode.getTotalLength() : 0;
  const totalLength = measured > 0 ? measured : 1000;
  lineNode.style.strokeDasharray = String(totalLength);
  lineNode.style.strokeDashoffset = String(totalLength);
  requestAnimationFrame(() => {
    lineNode.style.transition = "stroke-dashoffset 300ms ease-out";
    lineNode.style.strokeDashoffset = "0";
  });

  // ── Axes ───────────────────────────────────────────────────────────────
  const xAxis = el("line", {
    x1: PAD_LEFT,
    y1: baselineY,
    x2: VIEW_W - PAD_RIGHT,
    y2: baselineY,
    stroke: "#94A3B8",
    "stroke-width": 1,
  });
  svg.appendChild(xAxis);

  // Accessible label
  const title = el("title");
  title.textContent = "Balance over time";
  svg.appendChild(title);
}
